<script lang="ts" setup>
import { useAuthStore } from '@/stores/authStore'
import Button from 'primevue/button'
import Menubar from 'primevue/menubar'
import Menu from 'primevue/menu'
import { watch, ref, onMounted, computed, nextTick } from 'vue'
import type { Ref } from 'vue'
import type { MenuItem } from 'primevue/menuitem'
import { useThemeStore } from '@/stores/themeStore'
import { useRouter, useRoute } from 'vue-router'
import ProfileModal from '@/components/modals/UserProfileModal.vue'
import ApiKeysModal from '@/components/modals/ApiKeysModal.vue'
import PaymentModal from '@/components/modals/PaymentModal.vue'
import AutoTradeTriggersListModal from '@/components/modals/AutoTradeTriggersListModal.vue'
// import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { LOGIN_ROUTER_PAGE } from '@/utils/consts'
import { useApiKeysStore } from '@/stores/apiKeysStore'
import { useNavHeightRefresher } from '@/composable/refreshers/useNavHeightRefresher'
import { ProgressSpinner } from 'primevue'

interface ExtendedMenuItem extends MenuItem {
  route?: object
}

const route = useRoute()
const router = useRouter()
const themeStore = useThemeStore()
const apiKeysStore = useApiKeysStore()
const authStore = useAuthStore()
const menuItems: Ref<ExtendedMenuItem[]> = ref([])
const swapArbAvailable = computed(() => 'futures' in authStore.currentAccessList || 'futures_free' in authStore.currentAccessList || 'futures_advanced' in authStore.currentAccessList)
const swapApiKeysAvailable = computed(() => 'futures' in authStore.currentAccessList || 'futures_advanced' in authStore.currentAccessList)
const dexArbAvailable = computed(() => 'dex_cex_free' in authStore.currentAccessList || 'dex_cex' in authStore.currentAccessList || 'dex_cex_advanced' in authStore.currentAccessList)
const dexAdvancedArbAvailable = computed(() => 'dex_cex_advanced' in authStore.currentAccessList)

const splashesAvailable = computed(() => swapArbAvailable.value || dexArbAvailable.value)
const markerStyle = ref({ left: '0px', opacity: 0, top: '16.26px', width: '0px', height: '0px' })
const navWrapper = ref<HTMLDivElement>()
const { navHeight, navHeightRefresher } = useNavHeightRefresher(navWrapper, 92)

const showServerTriggers = computed(() => {
  return swapArbAvailable
})

onMounted(async () => {
  await checkUser()
  await apiKeysStore.fetchKeys()
  navHeightRefresher.startRefresh()
  setInterval(() => {
    updateMarkerStyles()
  }, 1000)
  themeStore?.setPaymentModalComponent(paymentModal.value)
})

const isDexCexPro = ref(false)

const checkUser = async () => {
  await authStore.check()
  if (!authStore.authorized) {
    router.push(LOGIN_ROUTER_PAGE)
    return
  }

  isDexCexPro.value = 'dex_cex' in authStore.currentAccessList || 'dex_cex_advanced' in authStore.currentAccessList
  const cexArbitrageMenuItems: MenuItem[] = [
    {
      label: 'oneLeg',
      items: [
        {
          label: 'spotSpot',
          route: { name: 'signals-spot-spot' },
          disabled: !swapArbAvailable.value,
          icon: !swapArbAvailable.value ? 'pi pi-lock' : '',
        },
        {
          label: 'swapSpot',
          route: { name: 'signals-swap-spot' },
          disabled: !swapArbAvailable.value,
          icon: !swapArbAvailable.value ? 'pi pi-lock' : '',
        },
      ],
    },
    {
      label: 'twoLegs',
      items: [
        {
          label: 'swap',
          route: { name: 'signals-swap' },
          disabled: !swapArbAvailable.value,
          icon: !swapArbAvailable.value ? 'pi pi-lock' : '',
        },
        {
          label: 'spotSwap',
          route: { name: 'signals-spot-swap' },
          disabled: !swapArbAvailable.value,
          icon: !swapArbAvailable.value ? 'pi pi-lock' : '',
        },
      ],
    },
    {
      label: 'other',
      items: [
        {
          label: 'fundingRates',
          route: { name: 'signals-funding-rates' },
        },
        {
          label: 'eyePanel',
          route: { name: 'eye-settings' },
          disabled: !swapArbAvailable.value,
          icon: !swapArbAvailable.value ? 'pi pi-lock' : '',
        },
      ],
    },
  ]
  const dexArbitrageMenuItems: MenuItem[] = [
    {
      label: 'dexCexSub',
      items: [
        {
          label: 'dexCexSwap',
          route: { name: 'signals-dex-cex-swap' },
          disabled: !dexArbAvailable.value,
          icon: !dexArbAvailable.value ? 'pi pi-lock' : '',
        },
        {
          label: 'dexCexSpot',
          route: { name: 'signals-dex-cex-spot' },
          disabled: !dexArbAvailable.value,
          icon: !dexArbAvailable.value ? 'pi pi-lock' : '',
        },
      ],
    },
    {
      label: 'dexDexSub',
      items: [
        {
          label: 'dexDex',
          route: { name: 'signals-dex-dex' },
          disabled: !dexAdvancedArbAvailable.value,
          icon: !dexAdvancedArbAvailable.value ? 'pi pi-lock' : '',
        },
        {
          label: 'dexDexCrosschain',
          route: { name: 'signals-dex-dex-crosschain' },
          disabled: !dexAdvancedArbAvailable.value,
          icon: !dexAdvancedArbAvailable.value ? 'pi pi-lock' : '',
        },
      ],
    },
    {
      label: 'other',
      items: [
        {
          label: 'eyeDexPanel',
          route: { name: 'eye-dex-settings' },
          disabled: !dexArbAvailable.value,
          icon: !dexArbAvailable.value ? 'pi pi-lock' : '',
        },
        {
          label: 'pools',
          route: { name: 'pools' },
          disabled: !dexArbAvailable.value,
          icon: !dexArbAvailable.value ? 'pi pi-lock' : '',
        },
      ],
    },
  ]
  menuItems.value = [
    {
      label: 'listings',
      route: { name: 'events' },
      icon: 'pi pi-calendar-clock',
    },
    {
      label: 'arbitrageCex',
      items: cexArbitrageMenuItems,
      icon: 'pi pi-sync',
    },
    {
      label: 'arbitrageDex',
      items: dexArbitrageMenuItems,
      icon: 'pi pi-sync',
    },
    {
      label: 'splash',
      disabled: !splashesAvailable.value,
      icon: !splashesAvailable.value ? 'pi pi-lock' : 'pi pi-wave-pulse',
      route: { name: 'signals-splash' },
    },
    {
      label: 'library',
      icon: 'pi pi-book',
      link: "https://docs.fobium.trade/"
    },
    // {
    //   label: 'funding',
    //   route: { name: 'funding' },
    //   icon: 'pi pi-percentage',
    // },
  ]
}
// md:rounded-xl md:m-3
const menuItemPt = {
  root: {
    class: 'sr-navbar-root m-0 rounded-none mb-0 md:mb-6',
  },
  button: {
    class: 'sr-navbar-button',
  },
  itemContent: {
    class: 'sr-navbar-item-content',
  },
  rootList: {
    class: 'sr-navbar-root-list',
  },
  submenu: {
    class: 'sr-navbar-submenu',
  },
}

const apiMenuItemPt = {
  root: {
    class: 'sr-navbar-root p-0 border-none rounded-none md:rounded-xl',
  },
  item: {
    class: 'cursor-pointer',
  },
  itemContent: {
    class: 'sr-navbar-button',
  },
}

watch(
  () => route.path,
  async () => {
    await nextTick()
    updateMarkerStyles()
  },
)

const colorSchemeIcon = computed(() => {
  if (themeStore.mode == 'dark') {
    return 'pi pi-moon'
  }
  return 'pi pi-sun'
})

const toggleApiKeysMenu = (event: Event) => apiKeysMenuRef.value.toggle(event)
const profileModal = ref()
const paymentModal = ref()
const apiKeysCexModal = ref()
const apiKeysDexModal = ref()
const autoTradeTriggersModal = ref()
const apiKeysMenuRef = ref()
const apiKeysMenu = [
  {
    label: 'apiKeyType.cexCex',
    command: () => {
      apiKeysCexModal.value.toggle()
    },
  },
  {
    label: 'apiKeyType.dexCex',
    command: () => {
      apiKeysDexModal.value.toggle()
    },
  },
]
const checkIsRoot = (item: MenuItem) => {
  if (
    item.label == 'arbitrageCex' &&
    route.name &&
    [
      'eye-settings',
      'signals-spot-swap',
      'signals-spot-spot',
      'signals-swap-spot',
      'signals-swap',
      'signals-funding-rates',
    ].includes(route.name?.toString())
  ) {
    return true
  }
  else if (
    item.label == 'arbitrageDex' &&
    route.name &&
    [
      'eye-dex-settings',
      'signals-dex-cex-swap',
      'signals-dex-cex-spot',
      'signals-dex-dex',
      'signals-dex-dex-crosschain',
      'pools',
    ].includes(route.name?.toString())
  ) {
    return true
  }
  return false
}
const updateMarkerStyles = () => {
  const target = document.querySelector('.p-menu-active')
  const menu = document.querySelector('.sr-navbar-root-list')
  if (!target?.parentElement || !menu) {
    return
  }
  const targetRect = target.parentElement.getBoundingClientRect()
  const top = targetRect.top
  const left = targetRect.left
  markerStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    width: `${targetRect.width}px`,
    height: `${targetRect.height}px`,
    opacity: 1,
  }
}

const logout = () => {
  authStore.logout()
  router.push(LOGIN_ROUTER_PAGE)
}

const isUserLoggedIn = computed(() => !authStore.loading.check && !authStore.loading.auth)
</script>

<template>
  <ApiKeysModal keys-type="cex_cex" ref="apiKeysCexModal"></ApiKeysModal>
  <ApiKeysModal :pro="isDexCexPro" keys-type="dex_cex" ref="apiKeysDexModal"></ApiKeysModal>
  <ProfileModal ref="profileModal"></ProfileModal>
  <PaymentModal ref="paymentModal"></PaymentModal>
  <AutoTradeTriggersListModal ref="autoTradeTriggersModal"></AutoTradeTriggersListModal>
  <!-- Шапка -->
  <div v-if="!isUserLoggedIn" class="w-full h-screen flex justify-center items-center align-center" key="spinner">
    <ProgressSpinner>
    </ProgressSpinner>
  </div>
  <template v-else>
    <div ref="navWrapper" class="sr-navbar-container">
      <!-- <div class="marker" :style="markerStyle"></div> -->
      <Menubar :model="menuItems" :pt="menuItemPt">
        <!-- <template #start>
        <img src="@/assets/navbar-logo.png" width="50px" height="50px" />
      </template> -->
        <template #item="{ item, hasSubmenu, root }">

          <a v-if="Boolean(item.link)" class="flex items-center gap-2 px-3 py-2 cursor-pointer" target="_blank"
            :href="item.link" :class="{ 'p-menu-active': checkIsRoot(item) }">
            <span :class="item.icon" v-if="item.icon" />
            <span>{{ $t(`navbar.${item.label}`) }}</span>
            <i v-if="hasSubmenu" :class="[
              'pi pi-angle-down ml-auto',
              { 'pi-angle-down': root, 'pi-angle-right': !root },
            ]"></i>
          </a>

          <span v-else-if="!Boolean(item.route)" class="flex items-center gap-2 px-3 py-2 cursor-pointer"
            :class="{ 'p-menu-active': checkIsRoot(item) }">
            <span :class="item.icon" v-if="item.icon" />
            <span>{{ $t(`navbar.${item.label}`) }}</span>
            <i v-if="hasSubmenu" :class="[
              'pi pi-angle-down ml-auto',
              { 'pi-angle-down': root, 'pi-angle-right': !root },
            ]"></i>
          </span>

          <router-link v-else :to="item.route" class="flex items-center gap-2 px-3 py-2" active-class="p-menu-active">
            <span :class="item.icon" v-if="item.icon" />
            <span>{{ $t(`navbar.${item.label}`) }}</span>
            <i v-if="hasSubmenu" :class="[
              'pi pi-angle-down ml-auto',
              { 'pi-angle-down': root, 'pi-angle-right': !root },
            ]"></i>
          </router-link>
        </template>
        <template #end>
          <div class="flex items-center gap-1 sr-navbar-end">
            <!-- <LanguageSwitcher /> -->
            <Button v-tooltip.bottom="$t('changeColorScheme')" :icon="colorSchemeIcon" @click="themeStore.toggle()"
              variant="text" rounded aria-label="Theme" />
            <Button v-tooltip.bottom="$t('myProfile')" icon="pi pi-user" @click="profileModal.toggle()" variant="text"
              rounded aria-label="Profile" />
            <Button v-tooltip.bottom="$t('modal.payment.title')" icon="pi pi-wallet" @click="paymentModal.toggle()"
              variant="text" rounded aria-label="Payment" />
            <Button v-tooltip.bottom="$t('serverTriggers')" icon="pi pi-bolt" @click="autoTradeTriggersModal.toggle()"
              v-if="showServerTriggers" variant="text" rounded aria-label="Server Triggers" />
            <template v-if="swapApiKeysAvailable && dexArbAvailable">
              <Button v-tooltip.bottom="$t('apiKeys')" icon="pi pi-key" @click="toggleApiKeysMenu" variant="text"
                rounded aria-label="Api Keys" aria-haspopup="true" aria-controls="overlay_menu" />
              <Menu id="overlay_menu" :popup="true" ref="apiKeysMenuRef" :model="apiKeysMenu" :pt="apiMenuItemPt">
                <template #item="{ item, props }">
                  <a class="p-menuitem-link" v-bind="props.action">
                    <span v-if="item.label">{{ $t(item.label as string) }}</span>
                  </a>
                </template>
              </Menu>
            </template>
            <Button v-tooltip.bottom="$t('apiKeys')" icon="pi pi-key" v-else-if="swapApiKeysAvailable"
              @click="apiKeysCexModal.toggle()" variant="text" rounded aria-label="Api Keys" />
            <Button v-tooltip.bottom="$t('apiKeys')" icon="pi pi-key" v-else-if="dexArbAvailable"
              @click="apiKeysDexModal.toggle()" variant="text" rounded aria-label="Api Keys" />
            <Button v-tooltip.bottom="$t('logout')" icon="pi pi-sign-out" @click="logout()" variant="text" rounded
              aria-label="Logout" />
          </div>
        </template>
      </Menubar>
    </div>
    <!-- Тело -->
    <main :style="{ paddingTop: navHeight + 'px' }" class="mx-0 md:mx-6 md:pb-1">
      <slot />
    </main>
  </template>
</template>
